import { LoginCredentials, RegisterData, User, ApiResponse, LoginResponse } from '../types';
import { API_ENDPOINTS } from '../config/apiConfig';

class AuthService {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('token');
    }
    return this.token;
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    try {
      const apiCredentials = {
        usernameOrEmail: credentials.email,
        password: credentials.password,
      };

      const response = await fetch(`${API_ENDPOINTS.authApi}/login`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include', // Include credentials for CORS
        body: JSON.stringify(apiCredentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.success && data.data.accessToken) {
        this.setToken(data.data.accessToken);
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Login failed');
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(`${API_ENDPOINTS.authApi}/change-password`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || 'Failed to change password');
    }

    return data;
  }

  async register(userData: RegisterData): Promise<ApiResponse<User>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.authApi}/register`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include', // Include credentials for CORS
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Registration failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${API_ENDPOINTS.authApi}/logout`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include', // Include credentials for CORS
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.removeToken();
    }
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.authApi}/me`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include', // Include credentials for CORS
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get user data');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get user data');
    }
  }

  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.authApi}/refresh-token`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include', // Include credentials for CORS
        body: JSON.stringify({ refreshToken: this.getToken() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Token refresh failed');
      }

      if (data.success && data.data.accessToken) {
        this.setToken(data.data.accessToken);
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Token refresh failed');
    }
  }
}

const authService = new AuthService();
export default authService; 